/**
 * Action cards that render inline in a Work transcript, directly under the turn
 * that asked for them — approving a risky browser action, answering a question,
 * reviewing a pinned plan, or buying credits when a job is too expensive. They
 * live in the message column so the ask stays attached to the reply that made it.
 *
 * Signing in is the exception: it is a form to type into, so it takes over the
 * composer instead (see WorkSignInComposer).
 */
import WorkCreditRecoveryCard from './WorkCreditRecoveryCard';
import WorkApprovalDetails from './WorkApprovalDetails';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, Platform, TextInput, Pressable, ActivityIndicator, ScrollView, Linking, Text as RNText } from 'react-native';
import PropTypes from 'prop-types';
import Markdown, { MarkdownIt } from 'react-native-markdown-display';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useToastStore } from '@/store';
import { coworkerApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { isDarkTheme, WARNING_COLORS } from '@/constants/Colors';
import {
  workAdvanceQuestionStep,
  workComposeQuestionAnswers,
  workHostOf,
  workPlanDecisionAnswer,
  settleWorkPlan,
  workPlanProgress,
  workPlanStatusLabel,
  workQuestionDraft,
  workQuestionStepLabel,
  workShouldSkipQuestionCard,
  isWorkScriptApprovalQuestion,
  isWorkClipApprovalQuestion,
  isWorkStillsApprovalQuestion,
  compactWorkQuestionMarkdown,
  workMarkdownLineHeight,
  isWorkAskTimeoutError,
  workScriptApprovalCardText,
  workClipApprovalCardText,
  workStillsApprovalCardText,
  workQuestionDisplayText,
} from '@/constants/work';
import { formatCreditBalance, formatCreditsInText } from '@/lib/formatCredits';
import useWorkQuestionDraft from '@/hooks/useWorkQuestionDraft';
import { workQuestionAnswerMeta } from '@/lib/workQuestionContinuation';

// Only answers short enough to read at a glance stay as chips in a wrap row.
// Anything longer becomes a stacked row so the list never goes ragged.
const CHIP_OPTION_CHARS = 18;
const NATIVE_HIT_SLOP = Platform.OS === 'web' ? undefined : 8;

// Keep the focused field from blurring on mousedown — that re-render used to
// cancel Pressable's click so the send arrow next to the box did nothing.
const WEB_KEEP_FOCUS = Platform.OS === 'web'
  ? { onMouseDown: (e) => e?.preventDefault?.() }
  : {};

const SAFE_MARKDOWN_URL = /^(https?:|mailto:)/i;
const questionMarkdownIt = MarkdownIt({ typographer: true, linkify: true, breaks: true }).disable(['image']);

function openQuestionMarkdownLink(url) {
  const trimmed = String(url || '').trim();
  if (!SAFE_MARKDOWN_URL.test(trimmed)) return false;
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.open(trimmed, '_blank', 'noopener,noreferrer');
  } else {
    Linking.openURL(trimmed).catch(() => {});
  }
  return false;
}

function restoreAskUnlessTimedOut(err, gateId, onRestoreQuestion, addToast, fallback) {
  if (isWorkAskTimeoutError(err)) {
    addToast('Still sending your answer to Work…', 'info');
    return;
  }
  onRestoreQuestion?.(gateId);
  addToast(err?.message || fallback, 'error');
}

function WorkQuestionMarkdown({ text, colors, fontSize = 15, color }) {
  const markdownStyles = useMemo(() => {
    const ink = color || colors.text_color_primary;
    const isWeb = Platform.OS === 'web';
    const lineHeight = workMarkdownLineHeight(fontSize * 1.4, isWeb);
    const webWrap = isWeb
      ? { wordBreak: 'break-word', overflowWrap: 'anywhere', userSelect: 'text' }
      : {};
    const headingLine = (extra) => workMarkdownLineHeight(fontSize + extra, isWeb);
    const headingBox = isWeb
      ? { marginBlockStart: 0, marginBlockEnd: 0, display: 'block' }
      : {};
    return {
      body: { color: ink, fontSize, lineHeight, ...webWrap },
      // Do not set fontSize/lineHeight on `text`. The library applies this
      // after inherited heading styles, which flattened ### Clip headings
      // back to body size so the card still looked unstyled.
      text: { color: ink, ...webWrap },
      paragraph: {
        marginTop: 0,
        marginBottom: 2,
        color: ink,
        fontSize,
        lineHeight,
        flexDirection: 'column',
        flexWrap: 'nowrap',
        width: '100%',
        ...webWrap,
      },
      heading1: { color: colors.text_color_primary, fontSize: fontSize + 4, fontWeight: '700', lineHeight: headingLine(8), marginTop: 6, marginBottom: 2, ...headingBox },
      heading2: { color: colors.text_color_primary, fontSize: fontSize + 3, fontWeight: '700', lineHeight: headingLine(7), marginTop: 6, marginBottom: 2, ...headingBox },
      heading3: { color: colors.text_color_primary, fontSize: fontSize + 1, fontWeight: '700', lineHeight: headingLine(6), marginTop: 5, marginBottom: 2, ...headingBox },
      heading4: { color: colors.text_color_primary, fontSize, fontWeight: '700', lineHeight: headingLine(4), marginTop: 5, marginBottom: 2, ...headingBox },
      heading5: { color: colors.text_color_primary, fontSize, fontWeight: '700', lineHeight: headingLine(4), marginTop: 4, marginBottom: 2, ...headingBox },
      heading6: { color: colors.text_color_primary, fontSize, fontWeight: '700', lineHeight: headingLine(4), marginTop: 4, marginBottom: 2, ...headingBox },
      strong: { fontWeight: '700', color: colors.text_color_primary },
      em: { fontStyle: 'italic' },
      s: { textDecorationLine: 'line-through' },
      bullet_list: { marginTop: 2, marginBottom: 4 },
      ordered_list: { marginTop: 2, marginBottom: 4 },
      list_item: { marginTop: 2, marginBottom: 2 },
      bullet_list_icon: { color: colors.text_color_primary, marginRight: 8 },
      ordered_list_icon: { color: colors.text_color_primary, marginRight: 8 },
      code_inline: {
        backgroundColor: colors.solid_color_secondary,
        color: colors.text_color_primary,
        paddingHorizontal: 5,
        paddingVertical: 1,
        borderRadius: 4,
        fontSize: fontSize - 1,
      },
      fence: {
        backgroundColor: colors.solid_color_secondary,
        color: colors.text_color_primary,
        padding: 10,
        borderRadius: 8,
        marginTop: 6,
        marginBottom: 8,
        fontSize: fontSize - 1,
      },
      code_block: {
        backgroundColor: colors.solid_color_secondary,
        color: colors.text_color_primary,
        padding: 10,
        borderRadius: 8,
        marginTop: 6,
        marginBottom: 8,
        fontSize: fontSize - 1,
      },
      blockquote: {
        backgroundColor: 'transparent',
        borderLeftColor: colors.border_color_secondary || 'rgba(255,255,255,0.12)',
        borderLeftWidth: 3,
        paddingLeft: 10,
        marginTop: 6,
        marginBottom: 6,
      },
      hr: {
        backgroundColor: colors.border_color_secondary,
        height: 1,
        marginTop: 6,
        marginBottom: 6,
      },
      link: {
        color: colors.text_color_link || colors.accent_text_color || colors.text_color_primary,
        textDecorationLine: 'underline',
      },
    };
  }, [color, colors, fontSize]);

  const markdownRules = useMemo(() => {
    // Native <h*> on web inherit UA margins and treat numeric lineHeight as a
    // CSS multiplier (21× font-size). RN Text goes through RN-web, which
    // emits px, so Clip headings stay tight instead of a blank screen.
    const heading = (style) => (node, children) => (
      <RNText key={node.key} style={style}>{children}</RNText>
    );
    return {
      heading1: heading(markdownStyles.heading1),
      heading2: heading(markdownStyles.heading2),
      heading3: heading(markdownStyles.heading3),
      heading4: heading(markdownStyles.heading4),
      heading5: heading(markdownStyles.heading5),
      heading6: heading(markdownStyles.heading6),
    };
  }, [markdownStyles]);

  return (
    <Markdown
      markdownit={questionMarkdownIt}
      style={markdownStyles}
      rules={markdownRules}
      onLinkPress={openQuestionMarkdownLink}
    >
      {compactWorkQuestionMarkdown(text)}
    </Markdown>
  );
}

WorkQuestionMarkdown.propTypes = {
  text: PropTypes.string.isRequired,
  colors: PropTypes.object.isRequired,
  fontSize: PropTypes.number,
  color: PropTypes.string,
};

function WorkQuestionBody({ text, colors, size = 'm', color }) {
  if (!String(text || '').trim()) return null;
  return (
    <WorkQuestionMarkdown
      text={text}
      colors={colors}
      fontSize={size === 's' ? 13 : 15}
      color={color === 'secondary' ? colors.text_color_secondary : colors.text_color_primary}
    />
  );
}

WorkQuestionBody.propTypes = {
  text: PropTypes.string,
  colors: PropTypes.object.isRequired,
  size: PropTypes.string,
  color: PropTypes.string,
};

function workSendPressProps(onSend, disabled) {
  return {
    disabled,
    // onPressIn fires before the input blurs; onPress stays for keyboard /
    // test-renderer. submit() is ref-guarded so both can fire.
    onPressIn: disabled ? undefined : onSend,
    onPress: disabled ? undefined : onSend,
    ...WEB_KEEP_FOCUS,
  };
}

/**
 * Surfaces for these cards. `accent_color_primary` is a *background* token —
 * on the black theme it defaults to #17191A — so nothing here paints text or
 * icons with it. The dark themes collapse `solid_color_block` into the page
 * background too, so they step up through the solid tokens and use translucent
 * white borders instead of `border_color_secondary`, which the page swallows.
 */
function accentWash(hex, alpha = 0.16) {
  const value = String(hex || '').replace('#', '');
  if (value.length !== 6) return `rgba(251, 191, 36, ${alpha})`;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function cardSurfaces(colors, isDark) {
  return {
    cardBg: isDark ? colors.solid_color_tertiary : colors.solid_color_block,
    cardBorder: isDark ? 'rgba(255, 255, 255, 0.10)' : colors.border_color_secondary,
    rowBg: isDark ? colors.solid_color_secondary : colors.solid_color_primary,
    rowBorder: isDark ? 'rgba(255, 255, 255, 0.09)' : colors.border_color_secondary,
    hoverBg: isDark ? colors.solid_color_tooltip : colors.solid_color_tertiary,
    hoverBorder: isDark ? 'rgba(255, 255, 255, 0.24)' : colors.border_color_input,
  };
}

export function WorkApprovalCard({ taskId, confirmation, onDismissQuestion, onRestoreQuestion }) {
  const { colors, theme } = useTheme();
  const surfaces = cardSurfaces(colors, isDarkTheme(theme));
  const { addToast } = useToastStore((s) => s.actions);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const decide = useCallback(async (approved) => {
    if (!confirmation || isSubmitting) return;
    const gateId = confirmation.id || '';
    setIsSubmitting(true);
    onDismissQuestion?.(gateId);
    try {
      await coworkerApi.confirmBrowserAction(taskId, { id: confirmation.id, approved });
      if (!approved) addToast('Action denied — Work will adjust', 'info');
    } catch (err) {
      restoreAskUnlessTimedOut(err, gateId, onRestoreQuestion, addToast, 'Failed to send decision');
    } finally {
      setIsSubmitting(false);
    }
  }, [confirmation, isSubmitting, onDismissQuestion, onRestoreQuestion, taskId, addToast]);

  if (!taskId || !confirmation) return null;

  const host = confirmation.url ? workHostOf(confirmation.url) : '';

  return (
    <View style={[styles.card, styles.approval, { borderColor: surfaces.cardBorder, backgroundColor: surfaces.cardBg }]}>
      <View style={[styles.approvalIcon, { backgroundColor: surfaces.rowBg }]}>
        <Icon name="shield-halved" size={15} color={colors.text_color_secondary} />
      </View>
      <View style={styles.approvalBody}>
        <View style={styles.approvalHeading}>
          <Text size="xs" weight="medium" color="secondary">Approval needed</Text>
          {!!host && (
            <View style={[styles.hostPill, { borderColor: surfaces.rowBorder }]}>
              <Icon name="globe" size={10} color={colors.text_color_secondary} />
              <Text size="xs" color="secondary" numberOfLines={1}>{host}</Text>
            </View>
          )}
        </View>
        <Text size="m" weight="semibold" style={styles.questionText}>Work needs your approval</Text>
        <WorkApprovalDetails key={confirmation.id} action={confirmation.action} detail={confirmation.detail} />
        <Text size="xs" color="tertiary">Only approve if this action matches what you asked Work to do.</Text>
        <View style={styles.approvalActions}>
          <Button text="Deny" size="small" variant="secondary" onPress={() => decide(false)} isDisabled={isSubmitting} />
          <Button text="Approve action" size="small" onPress={() => decide(true)} isDisabled={isSubmitting} isLoading={isSubmitting} />
        </View>
      </View>
    </View>
  );
}

WorkApprovalCard.propTypes = {
  taskId: PropTypes.string,
  confirmation: PropTypes.object,
  onDismissQuestion: PropTypes.func,
  onRestoreQuestion: PropTypes.func,
};

export function WorkCreditOffer({ estimatedCredits }) {
  const { colors, theme } = useTheme();
  const surfaces = cardSurfaces(colors, isDarkTheme(theme));
  const totalAvailable = useAuthStore(s => s.user?.credits?.totalAvailable ?? 0);
  const cost = Number(estimatedCredits) > 0 ? Math.ceil(Number(estimatedCredits)) : 0;
  const tooLow = cost > totalAvailable;
  const [openedShort] = useState(tooLow);
  const isCovered = openedShort && !tooLow;

  if (tooLow) return <WorkCreditRecoveryCard mode="step" requiredCredits={cost} />;

  return (
    <View style={[styles.creditBox, { backgroundColor: surfaces.rowBg, borderColor: surfaces.rowBorder }]}>
      <View style={styles.creditHeader}>
        {isCovered ? (
          <View style={styles.creditHeadline}>
            <Icon name="circle-check" size={13} color={colors.color_success} />
            <Text size="s" weight="semibold" style={{ color: colors.color_success }}>Balance updated — you&apos;re covered</Text>
          </View>
        ) : <Text size="s" weight="semibold">{cost > 0 ? `Costs about ${formatCreditBalance(cost)} credits` : 'Credit balance'}</Text>}
        <Text size="xs" color="tertiary">{formatCreditBalance(totalAvailable)} available</Text>
      </View>
      {isCovered && <Text size="xs" color="secondary">Choose an option below to continue.</Text>}
    </View>
  );
}

WorkCreditOffer.propTypes = {
  estimatedCredits: PropTypes.number,
};

/**
 * Suggested answers plus the typed-answer field for the question currently
 * on screen. Tapping an option submits that step immediately — on a
 * multi-question card that advances to the next one.
 */
function WorkQuestionChoices({
  options = [],
  allowCustom,
  custom,
  onCustomChange,
  selectedLabel,
  isFocused,
  onFocusChange,
  pendingKey,
  isSubmitting,
  onChooseOption,
  onSubmitCustom,
  surfaces,
  colors,
  answerLabel,
  answerPlaceholder,
  emphasizeFirst,
}) {
  const asChips = options.length > 0
    && options.every((o) => String(o.label).length <= CHIP_OPTION_CHARS);
  const canSend = !!String(custom || '').trim();
  const sendCustom = () => onSubmitCustom(custom);

  return (
    <>
      {options.length > 0 && (
        <View style={asChips ? styles.optionWrap : styles.optionStack}>
          {options.map((opt, index) => {
            const key = opt.id || opt.label;
            const isPending = pendingKey === key;
            const isSelected = !isPending && selectedLabel === opt.label;
            const isPrimary = emphasizeFirst && index === 0;
            const primaryInk = colors.solid_color_primary;
            return (
              <Pressable
                key={key}
                disabled={isSubmitting}
                accessibilityRole="button"
                accessibilityLabel={opt.label}
                accessibilityState={{ selected: isSelected, busy: isPending }}
                hitSlop={NATIVE_HIT_SLOP}
                onPress={() => onChooseOption(opt.label, opt.id, key)}
                style={({ hovered, pressed }) => [
                  asChips ? styles.optionChip : styles.optionRow,
                  isPrimary
                    ? { borderColor: colors.text_color_primary, backgroundColor: colors.text_color_primary }
                    : { borderColor: surfaces.rowBorder, backgroundColor: surfaces.rowBg },
                  (hovered || pressed) && !isSubmitting && !isPrimary && { borderColor: surfaces.hoverBorder, backgroundColor: surfaces.hoverBg },
                  (hovered || pressed) && !isSubmitting && isPrimary && { opacity: 0.9 },
                  (isPending || isSelected) && !isPrimary && { borderColor: isSelected ? colors.text_color_primary : surfaces.hoverBorder, backgroundColor: surfaces.hoverBg },
                  isSubmitting && !isPending && styles.optionMuted,
                ]}
              >
                {(!asChips || isPending || isSelected) && (
                  <View style={asChips ? styles.chipSpinner : [styles.optionIndex, { borderColor: isPrimary ? `${primaryInk}59` : surfaces.rowBorder }]}>
                    {isPending
                      ? <ActivityIndicator size="small" color={isPrimary ? primaryInk : colors.text_color_secondary} style={styles.optionSpinner} />
                      : (isSelected && asChips
                        ? <Icon name="check" size={11} color={colors.text_color_primary} />
                        : <Text size="xs" color={isPrimary ? undefined : 'secondary'} style={isPrimary ? { color: primaryInk } : undefined}>{index + 1}</Text>)}
                  </View>
                )}
                <Text size="s" weight="medium" style={[styles.optionLabel, isPrimary && { color: primaryInk }]}>{opt.label}</Text>
              </Pressable>
            );
          })}
        </View>
      )}

      {allowCustom && (
        <View style={[styles.customBox, {
          borderColor: isFocused ? surfaces.hoverBorder : surfaces.rowBorder,
          backgroundColor: surfaces.rowBg,
        }]}>
          <View style={styles.customInputWrap}>
            <TextInput
              value={custom}
              onChangeText={onCustomChange}
              placeholder={answerPlaceholder || (options.length > 0 ? 'Or type your own answer…' : 'Type your answer…')}
              placeholderTextColor={colors.color_input_placeholder}
              accessibilityLabel={answerLabel || 'Your own answer'}
              editable={!isSubmitting}
              onFocus={() => onFocusChange(true)}
              onBlur={() => onFocusChange(false)}
              onSubmitEditing={sendCustom}
              blurOnSubmit
              returnKeyType="send"
              style={[styles.customInput, { color: colors.text_color_primary }]}
            />
          </View>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Send answer"
            hitSlop={NATIVE_HIT_SLOP}
            {...workSendPressProps(sendCustom, isSubmitting || !canSend)}
            style={({ hovered }) => [
              styles.sendButton,
              { backgroundColor: canSend ? colors.text_color_primary : surfaces.hoverBg },
              hovered && canSend && styles.sendButtonHovered,
            ]}
          >
            {pendingKey === 'custom'
              ? <ActivityIndicator size="small" color={colors.text_color_alt} style={styles.optionSpinner} />
              : <Icon name="arrow-up" size={11} color={canSend ? colors.text_color_alt : colors.text_color_secondary} />}
          </Pressable>
        </View>
      )}
    </>
  );
}

WorkQuestionChoices.propTypes = {
  options: PropTypes.array,
  allowCustom: PropTypes.bool,
  custom: PropTypes.string,
  onCustomChange: PropTypes.func.isRequired,
  selectedLabel: PropTypes.string,
  isFocused: PropTypes.bool,
  onFocusChange: PropTypes.func.isRequired,
  pendingKey: PropTypes.string,
  isSubmitting: PropTypes.bool,
  onChooseOption: PropTypes.func.isRequired,
  onSubmitCustom: PropTypes.func.isRequired,
  surfaces: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  answerLabel: PropTypes.string,
  emphasizeFirst: PropTypes.bool,
};

/**
 * One ask_user card. Several sub-questions show one at a time — tap an option
 * (or send a typed answer) to advance, then we POST the collected answers.
 * Parent remounts this when `question.id` changes (see ChatView).
 */
export function WorkQuestionCard({ taskId, question, onAnswered, onDismissQuestion, onRestoreQuestion }) {
  const { colors, theme } = useTheme();
  const { addToast } = useToastStore((s) => s.actions);
  const userId = useAuthStore(s => s.user?.id || s.user?._id);
  const { custom, answers, stepIndex, setCustom, setAnswers, setStepIndex, clearDraft } = useWorkQuestionDraft(userId, taskId, question);
  // Which control is in flight, so only that one shows a spinner and the rest
  // dim instead of every option looking half-pressed at once.
  const [pendingKey, setPendingKey] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const submittingRef = useRef(false);
  const isSubmitting = pendingKey !== null;

  const options = useMemo(
    () => (Array.isArray(question?.options) ? question.options.filter((o) => o?.label) : []),
    [question],
  );
  const subQuestions = useMemo(
    () => (Array.isArray(question?.questions) ? question.questions.filter((q) => q?.question) : []),
    [question],
  );
  const isMulti = subQuestions.length > 1;
  const current = isMulti ? subQuestions[Math.min(stepIndex, subQuestions.length - 1)] : null;
  const currentOptions = isMulti
    ? (Array.isArray(current?.options) ? current.options.filter((o) => o?.label) : [])
    : options;
  const selectedLabel = isMulti && !String(custom || '').trim()
    ? workQuestionDraft(current, answers[current?.id]).selectedLabel
    : '';
  const allowCustom = question?.allowCustom !== false;
  const isCredits = question?.kind === 'credits';
  const isCost = question?.kind === 'cost';
  const isMemory = question?.kind === 'memory';
  const isSpendGate = isCredits || isCost;
  const isApprovalGate = isSpendGate || isMemory;
  const surfaces = cardSurfaces(colors, isDarkTheme(theme));
  const isScriptApproval = isWorkScriptApprovalQuestion(question);
  const isClipApproval = isWorkClipApprovalQuestion(question);
  const isStillsApproval = isWorkStillsApprovalQuestion(question);
  const isComponentReview = ['script', 'voice', 'narration', 'music', 'visuals', 'mix'].includes(question?.mediaReview?.component);
  const isRequiredApproval = isComponentReview || isScriptApproval || isClipApproval || isStillsApproval || question?.required === true || question?.historical === true;
  const isMediaGate = isComponentReview || isClipApproval || isStillsApproval;
  const intro = workQuestionDisplayText(
    isScriptApproval
      ? workScriptApprovalCardText(question?.question)
      : isClipApproval
        ? workClipApprovalCardText(question?.question)
        : isStillsApproval
          ? workStillsApprovalCardText(question?.question)
          : question?.question,
  );
  const stepPrompt = workQuestionDisplayText(
    isMulti
      ? (current?.question || 'Work needs your answer')
      : (intro || 'Work needs your answer'),
  );
  const prompt = isScriptApproval
    ? workScriptApprovalCardText(stepPrompt)
    : isClipApproval
      ? workClipApprovalCardText(stepPrompt)
      : isStillsApproval
        ? workStillsApprovalCardText(stepPrompt)
        : stepPrompt;
  const showIntro = !isScriptApproval && !isClipApproval && !isStillsApproval && isMulti && stepIndex === 0 && intro && intro !== prompt;

  const applyDraft = useCallback((item, stored) => {
    const next = workQuestionDraft(item, stored);
    setCustom(next.custom);
    setIsFocused(false);
  }, [setCustom]);

  const submit = useCallback(async (answer, optionId, key) => {
    const text = String(answer || '').trim();
    if (!taskId || !question || submittingRef.current || !text) return;
    const gateId = question.id || '';
    submittingRef.current = true;
    setPendingKey(key || optionId || 'custom');
    onDismissQuestion?.(gateId);
    try {
      // A pause dismisses the waiter. POST /ask 404s; ChatView resumes
      // with this text so Approve still delivers after the user read the script.
      const response = !question.orphaned
        ? await coworkerApi.answerQuestion(taskId, { id: question.id || '', answer: text, optionId, ...(question.historical ? { historical: true } : {}) })
        : null;
      await onAnswered?.(text, workQuestionAnswerMeta(response));
      clearDraft();
    } catch (err) {
      restoreAskUnlessTimedOut(err, gateId, onRestoreQuestion, addToast, 'Failed to send answer');
    } finally {
      submittingRef.current = false;
      setPendingKey(null);
    }
  }, [addToast, clearDraft, onAnswered, onDismissQuestion, onRestoreQuestion, question, taskId]);

  // Skipping is an answer, not a dismissal: the agent is told to pick a
  // sensible default and keep going rather than asking again.
  const skipCard = useCallback(async () => {
    if (!taskId || !question || submittingRef.current) return;
    const gateId = question.id || '';
    submittingRef.current = true;
    setPendingKey('skip');
    onDismissQuestion?.(gateId);
    try {
      const response = !question.orphaned
        ? await coworkerApi.answerQuestion(taskId, { id: question.id || '', skipped: true })
        : null;
      await onAnswered?.('', workQuestionAnswerMeta(response, true));
      clearDraft();
    } catch (err) {
      restoreAskUnlessTimedOut(err, gateId, onRestoreQuestion, addToast, 'Failed to skip the question');
    } finally {
      submittingRef.current = false;
      setPendingKey(null);
    }
  }, [addToast, clearDraft, onAnswered, onDismissQuestion, onRestoreQuestion, question, taskId]);

  const finishMulti = useCallback((nextAnswers) => {
    if (workShouldSkipQuestionCard(subQuestions, nextAnswers)) {
      skipCard();
      return;
    }
    submit(workComposeQuestionAnswers(subQuestions, nextAnswers), undefined, 'all');
  }, [skipCard, subQuestions, submit]);

  const recordAndAdvance = useCallback((answer) => {
    if (!isMulti || !current || submittingRef.current) return;
    const nextAnswers = { ...answers, [current.id]: String(answer || '').trim() };
    const next = workAdvanceQuestionStep(subQuestions.length, stepIndex);
    if (next === null) {
      finishMulti(nextAnswers);
      return;
    }
    setAnswers(nextAnswers);
    setStepIndex(next);
    applyDraft(subQuestions[next], nextAnswers[subQuestions[next]?.id]);
  }, [answers, applyDraft, current, finishMulti, isMulti, stepIndex, subQuestions]);

  const skip = useCallback(() => {
    if (isRequiredApproval) return;
    if (submittingRef.current) return;
    if (!isMulti) {
      skipCard();
      return;
    }
    if (!current) return;
    const nextAnswers = { ...answers, [current.id]: '' };
    const next = workAdvanceQuestionStep(subQuestions.length, stepIndex);
    if (next === null) {
      finishMulti(nextAnswers);
      return;
    }
    setAnswers(nextAnswers);
    setStepIndex(next);
    applyDraft(subQuestions[next], nextAnswers[subQuestions[next]?.id]);
  }, [answers, applyDraft, current, finishMulti, isMulti, isRequiredApproval, skipCard, stepIndex, subQuestions]);

  const goBack = useCallback(() => {
    if (!isMulti || stepIndex <= 0 || isSubmitting) return;
    // Keep the unfinished answer too: Back must not discard what was typed
    // on the current step before the user pressed Send.
    if (current && String(custom || '').trim()) {
      setAnswers((previous) => ({ ...previous, [current.id]: custom }));
    }
    const prev = stepIndex - 1;
    setStepIndex(prev);
    applyDraft(subQuestions[prev], answers[subQuestions[prev]?.id]);
  }, [answers, applyDraft, current, custom, isMulti, isSubmitting, stepIndex, subQuestions]);

  const chooseOption = useCallback((label, optionId, key) => {
    if (isMulti) recordAndAdvance(label);
    else submit(label, optionId, key);
  }, [isMulti, recordAndAdvance, submit]);

  const submitCustom = useCallback((text) => {
    if (!String(text || '').trim()) return;
    if (isMulti) recordAndAdvance(text);
    else submit(text, undefined, 'custom');
  }, [isMulti, recordAndAdvance, submit]);

  if (!taskId || !question) return null;

  if (question.kind === 'plan') {
    return (
      <WorkPlanCard
        taskId={taskId}
        plan={question.plan}
        question={question}
        onAnswered={onAnswered}
        onDismissQuestion={onDismissQuestion}
        onRestoreQuestion={onRestoreQuestion}
      />
    );
  }

  if (isApprovalGate) {
    // The memory gate is not a warning, so it stays neutral rather than amber.
    const gateColor = isSpendGate ? WARNING_COLORS.base : colors.text_color_secondary;
    return (
      <View style={[styles.card, styles.approval, { borderColor: surfaces.cardBorder, backgroundColor: surfaces.cardBg }]}>
        <View style={[styles.approvalIcon, { backgroundColor: isSpendGate ? `${gateColor}22` : surfaces.rowBg }]}>
          <Icon name={isSpendGate ? 'coins' : 'brain'} size={15} color={gateColor} />
        </View>
        <View style={styles.approvalBody}>
          <Text size="xs" weight="bold" style={[styles.eyebrow, { color: gateColor }]}>
            {isCredits ? 'CREDITS' : 'APPROVAL NEEDED'}
          </Text>
          <Text size="m" weight="semibold" style={styles.questionText}>
            {isMemory
              ? 'Work wants to update your memory'
              : (isCredits ? 'Not enough credits to start' : 'Work needs your approval')}
          </Text>
          <Text size="s" color="secondary">{formatCreditsInText(question.question)}</Text>
          {isSpendGate && <WorkCreditOffer estimatedCredits={question.estimatedCredits} />}
          <Text size="xs" color="tertiary">
            {isMemory
              ? 'This writes to your Memory page. Approve only if you asked Work to remember, change, or forget this.'
              : (isCredits
                ? 'Add credits, then approve to continue — or cancel this step.'
                : 'Large generations spend credits even when your balance covers them.')}
          </Text>
          <View style={styles.approvalActions}>
            <Button
              text={isMemory ? "Don't save" : "Don't start"}
              size="small"
              variant="secondary"
              accessibilityLabel={isMemory ? "Don't save" : "Don't start"}
              onPress={() => submit(isMemory ? "Don't save" : "Don't start", 'opt-1')}
              isDisabled={isSubmitting}
            />
            <Button
              text={isMemory ? 'Approve' : (isCredits ? 'Continue after purchase' : 'Approve spend')}
              size="small"
              prependIcon={isCredits ? 'check' : undefined}
              accessibilityLabel={isMemory ? 'Approve' : (isCredits ? 'Continue after purchase' : 'Approve spend')}
              onPress={() => submit(isCredits ? "I'll add credits, then continue" : 'Approve', 'opt-0')}
              isDisabled={isSubmitting}
              isLoading={isSubmitting}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.questionCard, {
      borderColor: isMediaGate ? colors.accent_color_primary : surfaces.cardBorder,
      backgroundColor: isMediaGate ? accentWash(colors.accent_color_primary, 0.16) : surfaces.cardBg,
      borderWidth: isMediaGate ? 2 : 1,
    }]}>
      <View style={styles.questionHeader}>
        <Icon
          name={isClipApproval ? 'film' : isStillsApproval ? 'image' : 'circle-question'}
          size={12}
          color={isMediaGate ? colors.text_color_primary : colors.text_color_secondary}
        />
        <Text
          size="xs"
          weight="bold"
          color={isMediaGate ? 'primary' : 'secondary'}
          style={styles.eyebrow}
        >
          {isMediaGate ? 'NEEDS YOUR APPROVAL' : isMulti ? workQuestionStepLabel(stepIndex, subQuestions.length) : 'QUESTION'}
        </Text>
        {isMulti && (
          <View
            style={styles.stepDots}
            accessibilityRole="progressbar"
            accessibilityLabel={workQuestionStepLabel(stepIndex, subQuestions.length)}
            accessibilityValue={{ min: 1, max: subQuestions.length, now: stepIndex + 1 }}
          >
            {subQuestions.map((item, i) => (
              <View
                key={item.id}
                style={[styles.stepDot, {
                  backgroundColor: i <= stepIndex ? colors.text_color_primary : surfaces.rowBorder,
                }]}
              />
            ))}
          </View>
        )}
      </View>
      <ScrollView
        key={isMulti ? stepIndex : question.id}
        style={styles.questionContentScroll}
        contentContainerStyle={styles.questionContent}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator
      >
        <View style={styles.questionBodyContent}>
          {showIntro && (
            <WorkQuestionBody text={intro} colors={colors} size="s" color="secondary" />
          )}
          <WorkQuestionBody text={prompt} colors={colors} size="s" />
          {question?.mediaReview?.component && <Text size="xs" color="secondary">
            Choose Approve to accept this version, or describe changes in the field below.
          </Text>}
          {question?.orphaned && isScriptApproval ? (
            <Text size="s" color="secondary" style={styles.questionBody}>
              Work asked this, then the run paused. Approve or Revise here to resume — the script is in the message above.
            </Text>
          ) : null}
          {question?.orphaned && isClipApproval ? (
            <Text size="s" color="secondary" style={styles.questionBody}>
              All clips are in. Approve here and Work will stitch the film, or redo a shot first.
            </Text>
          ) : null}
          {question?.orphaned && isStillsApproval ? (
            <Text size="s" color="secondary" style={styles.questionBody}>
              Check that every character looks consistent. Approve to animate, or redo a still first.
            </Text>
          ) : null}
        </View>

        <View style={styles.questionActions}>
          <WorkQuestionChoices
            options={currentOptions}
            allowCustom={allowCustom}
            custom={custom}
            onCustomChange={(text) => {
              setCustom(text);
              // Empty is an intentional edit too; otherwise Back resurrects the
              // previously stored answer after the user clears the input.
              if (isMulti && current) setAnswers(previous => ({ ...previous, [current.id]: text }));
            }}
            selectedLabel={selectedLabel}
            isFocused={isFocused}
            onFocusChange={setIsFocused}
            pendingKey={pendingKey}
            isSubmitting={isSubmitting}
            onChooseOption={chooseOption}
            onSubmitCustom={submitCustom}
            surfaces={surfaces}
            colors={colors}
            answerLabel={isMulti ? `Answer to question ${stepIndex + 1}` : question?.mediaReview?.component ? 'Requested changes' : 'Your own answer'}
            answerPlaceholder={question?.mediaReview?.component ? 'Describe changes to this version…' : undefined}
            emphasizeFirst={isMediaGate}
          />
        </View>

        <View style={[styles.questionFooter, styles.questionActions]}>
          {isMulti && stepIndex > 0 && (
            <Pressable
              disabled={isSubmitting}
              accessibilityRole="button"
              accessibilityLabel="Previous question"
              hitSlop={NATIVE_HIT_SLOP}
              onPress={goBack}
              style={({ hovered, pressed }) => [
                styles.skipButton,
                { borderColor: surfaces.rowBorder },
                (hovered || pressed) && !isSubmitting && { borderColor: surfaces.hoverBorder, backgroundColor: surfaces.hoverBg },
              ]}
            >
              <Icon name="angle-left" size={12} color={colors.text_color_secondary} />
              <Text size="xs" weight="medium" color="secondary">Back</Text>
            </Pressable>
          )}
          {isRequiredApproval ? null : (
            <Pressable
              disabled={isSubmitting}
              accessibilityRole="button"
              accessibilityLabel="Skip this question"
              hitSlop={NATIVE_HIT_SLOP}
              onPress={skip}
              style={({ hovered, pressed }) => [
                styles.skipButton,
                { borderColor: surfaces.rowBorder },
                (hovered || pressed) && !isSubmitting && { borderColor: surfaces.hoverBorder, backgroundColor: surfaces.hoverBg },
                isSubmitting && pendingKey !== 'skip' && styles.optionMuted,
              ]}
            >
              {pendingKey === 'skip'
                ? <ActivityIndicator size="small" color={colors.text_color_secondary} style={styles.optionSpinner} />
                : <Icon name="angle-right" size={12} color={colors.text_color_secondary} />}
              <Text size="xs" weight="medium" color="secondary">Skip</Text>
            </Pressable>
          )}
          {isRequiredApproval ? null : (
            <Text size="xs" color="tertiary" style={styles.skipHint}>
              {isMulti && stepIndex < subQuestions.length - 1
                ? 'Skip this one — Work picks a default, then the next question.'
                : 'Work will pick a sensible default and keep going.'}
            </Text>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

WorkQuestionCard.propTypes = {
  taskId: PropTypes.string,
  question: PropTypes.object,
  onAnswered: PropTypes.func,
  onDismissQuestion: PropTypes.func,
  onRestoreQuestion: PropTypes.func,
};

const PLAN_STEP_ICON = {
  done: 'circle-check',
  skipped: 'circle-minus',
  in_progress: 'circle-dot',
  blocked: 'circle-exclamation',
  pending: 'circle',
};

/**
 * Plan document attached to the Work thread. Collapsed it reads like a file
 * (title + View plan / Build). Expanded it is the live checklist. Ambiguous
 * work auto-expands and waits for Build; bounded work runs without stalling.
 */
export function WorkPlanCard({ taskId, plan, question, onAnswered, onDismissQuestion, onRestoreQuestion, onViewInteraction, readOnly = false }) {
  const { colors, theme } = useTheme();
  const surfaces = cardSurfaces(colors, isDarkTheme(theme));
  const { addToast } = useToastStore((s) => s.actions);
  const [pendingKey, setPendingKey] = useState(null);
  const [changes, setChanges] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [viewing, setViewing] = useState(() => !!(question && question.kind === 'plan'));
  const submittingRef = useRef(false);
  const isSubmitting = pendingKey !== null;
  const awaitingApproval = !readOnly && !!question && question.kind === 'plan';
  const settledPlan = useMemo(() => settleWorkPlan(plan), [plan]);

  const steps = useMemo(
    () => (Array.isArray(settledPlan?.steps) ? settledPlan.steps.filter((s) => s?.title) : []),
    [settledPlan],
  );
  const progress = useMemo(() => workPlanProgress({ steps }), [steps]);
  const isOpen = awaitingApproval || viewing;
  const focusStep = useMemo(() => (
    steps.find((s) => s.status === 'in_progress')
    || steps.find((s) => s.status === 'blocked')
    || steps.find((s) => !s.status || s.status === 'pending')
    || null
  ), [steps]);

  useEffect(() => {
    if (awaitingApproval) setViewing(true);
  }, [awaitingApproval]);

  const answer = useCallback(async (decision, text, key) => {
    if (readOnly || !taskId || !question || submittingRef.current) return;
    const payload = workPlanDecisionAnswer(decision, text);
    const gateId = question.id || '';
    submittingRef.current = true;
    setPendingKey(key);
    onDismissQuestion?.(gateId);
    try {
      const response = await coworkerApi.answerQuestion(taskId, { id: question.id || '', ...payload, ...(question.historical ? { historical: true } : {}) });
      setChanges('');
      const replied = String(payload.answer || text || '').trim();
      await onAnswered?.(replied, workQuestionAnswerMeta(response));
    } catch (err) {
      restoreAskUnlessTimedOut(err, gateId, onRestoreQuestion, addToast, 'Failed to send your decision');
    } finally {
      submittingRef.current = false;
      setPendingKey(null);
    }
  }, [addToast, onAnswered, onDismissQuestion, onRestoreQuestion, question, readOnly, taskId]);

  const toggleView = useCallback(() => {
    if (awaitingApproval) return;
    onViewInteraction?.();
    setViewing((open) => !open);
  }, [awaitingApproval, onViewInteraction]);

  if (!settledPlan || !steps.length) return null;

  const statusLabel = workPlanStatusLabel(settledPlan, { awaitingApproval });
  const accent = awaitingApproval ? WARNING_COLORS.base : colors.text_color_secondary;
  const canSendChanges = !!changes.trim();
  const title = settledPlan.goal || 'Plan';

  return (
    <View
      style={[
        styles.planDoc,
        awaitingApproval && styles.planDocGate,
        { borderColor: surfaces.cardBorder, backgroundColor: surfaces.cardBg },
      ]}
    >
      <Pressable
        onPressIn={onViewInteraction}
        onPress={toggleView}
        disabled={awaitingApproval}
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen }}
        accessibilityLabel={isOpen ? 'Hide plan' : `View plan: ${title}`}
        style={({ hovered, pressed }) => [
          styles.planFile,
          !awaitingApproval && (hovered || pressed) && { backgroundColor: surfaces.hoverBg },
        ]}
      >
        <View style={[styles.planFileIcon, { backgroundColor: surfaces.rowBg }]}>
          <Icon name="file-lines" size={15} color={accent} />
        </View>
        <View style={styles.planFileCopy}>
          <View style={styles.planFileTitleRow}>
            <Text size="xs" weight="bold" color="secondary" style={styles.eyebrow}>PLAN</Text>
            <Text size="xs" color="tertiary">{statusLabel}</Text>
          </View>
          <Text size="s" weight="semibold" numberOfLines={isOpen ? 3 : 2} ellipsizeMode="tail">
            {title}
          </Text>
          {!isOpen && !!focusStep?.title && focusStep.title !== title && (
            <Text size="xs" color="tertiary" numberOfLines={1} ellipsizeMode="tail">
              {focusStep.title}
            </Text>
          )}
        </View>
      </Pressable>

      {isOpen && (
        <>
          <View
            style={[styles.planTrack, { backgroundColor: surfaces.rowBg }]}
            accessibilityRole="progressbar"
            accessibilityLabel={`${progress.done} of ${progress.total} plan steps complete`}
            accessibilityValue={{ min: 0, max: progress.total, now: progress.done }}
          >
            <View style={[styles.planFill, {
              width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`,
              backgroundColor: progress.allDone ? colors.color_success || colors.text_color_primary : colors.text_color_primary,
            }]} />
          </View>
          <ScrollView
            style={styles.planStepsScroll}
            contentContainerStyle={styles.planSteps}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
          >
            {steps.map((step) => {
              const status = step.status || 'pending';
              const isDone = status === 'done' || status === 'skipped';
              const isBlocked = status === 'blocked';
              const isLive = status === 'in_progress';
              const iconColor = isBlocked
                ? colors.color_destructive
                : (isLive ? colors.text_color_primary : (isDone ? colors.text_color_secondary : colors.text_color_tertiary));
              return (
                <View key={step.id || step.title} style={styles.planStep}>
                  <Icon name={PLAN_STEP_ICON[status] || 'circle'} size={13} color={iconColor} style={styles.planStepIcon} />
                  <View style={styles.planStepBody}>
                    <Text
                      size="s"
                      weight={isLive ? 'semibold' : 'regular'}
                      color={isDone ? 'secondary' : undefined}
                      style={isDone ? styles.planStepDone : undefined}
                    >
                      {step.title}
                    </Text>
                    {!!step.note && (
                      <Text size="xs" color={isBlocked ? undefined : 'tertiary'} style={isBlocked && { color: colors.color_destructive }}>
                        {step.note}
                      </Text>
                    )}
                    {Array.isArray(step.files) && step.files.filter(file => typeof file === 'string').slice(0, 12).map(file => (
                      <Text key={file} size="xs" color="tertiary" selectable>{file}</Text>
                    ))}
                  </View>
                </View>
              );
            })}
          </ScrollView>
        </>
      )}

      <View style={styles.planActions}>
        {!awaitingApproval && (
          <Pressable
            onPressIn={onViewInteraction}
            onPress={toggleView}
            accessibilityRole="button"
            accessibilityLabel={isOpen ? 'Hide plan' : 'View plan'}
            accessibilityState={{ expanded: isOpen }}
            hitSlop={NATIVE_HIT_SLOP}
            style={({ hovered, pressed }) => [
              styles.planAction,
              { borderColor: surfaces.rowBorder },
              (hovered || pressed) && { borderColor: surfaces.hoverBorder, backgroundColor: surfaces.hoverBg },
            ]}
          >
            <Icon name={isOpen ? 'chevron-up' : 'arrow-up-right-from-square'} size={11} color={colors.text_color_secondary} />
            <Text size="xs" weight="medium" color="secondary">{isOpen ? 'Hide plan' : 'View plan'}</Text>
          </Pressable>
        )}
        {awaitingApproval && (
          <>
            <Text size="xs" color="tertiary" style={styles.planHint}>
              Work waits here until you Build. Request changes if this is the wrong approach.
            </Text>
            <View style={[styles.customBox, {
              borderColor: isFocused ? surfaces.hoverBorder : surfaces.rowBorder,
              backgroundColor: surfaces.rowBg,
            }]}>
              <View style={styles.customInputWrap}>
                <TextInput
                  value={changes}
                  onChangeText={setChanges}
                  placeholder="Change the plan… (optional)"
                  placeholderTextColor={colors.color_input_placeholder}
                  accessibilityLabel="Requested plan changes"
                  editable={!isSubmitting}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  onSubmitEditing={() => answer('changes', changes, 'changes')}
                  blurOnSubmit
                  returnKeyType="send"
                  style={[styles.customInput, { color: colors.text_color_primary }]}
                />
              </View>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Request these changes"
                hitSlop={NATIVE_HIT_SLOP}
                {...workSendPressProps(() => answer('changes', changes, 'changes'), isSubmitting || !canSendChanges)}
                style={({ hovered }) => [
                  styles.sendButton,
                  { backgroundColor: canSendChanges ? colors.text_color_primary : surfaces.hoverBg },
                  hovered && canSendChanges && styles.sendButtonHovered,
                ]}
              >
                {pendingKey === 'changes'
                  ? <ActivityIndicator size="small" color={colors.text_color_alt} style={styles.optionSpinner} />
                  : <Icon name="arrow-up" size={11} color={canSendChanges ? colors.text_color_alt : colors.text_color_secondary} />}
              </Pressable>
            </View>
            <View style={styles.approvalActions}>
              <Button
                text="Request changes"
                size="small"
                variant="secondary"
                onPress={() => answer('changes', changes, 'changes')}
                isDisabled={isSubmitting}
                isLoading={pendingKey === 'changes'}
              />
              <Button
                text="Build"
                size="small"
                prependIcon="check"
                accessibilityLabel="Build"
                onPress={() => answer('approve', '', 'approve')}
                isDisabled={isSubmitting}
                isLoading={pendingKey === 'approve'}
              />
            </View>
          </>
        )}
      </View>
    </View>
  );
}

WorkPlanCard.propTypes = {
  readOnly: PropTypes.bool,
  taskId: PropTypes.string,
  plan: PropTypes.object,
  question: PropTypes.object,
  onAnswered: PropTypes.func,
  onDismissQuestion: PropTypes.func,
  onRestoreQuestion: PropTypes.func,
  onViewInteraction: PropTypes.func,
};

const styles = StyleSheet.create({
  planDoc: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 10,
    gap: 8,
    ...Platform.select({ web: { cursor: 'default' } }),
  },
  planDocGate: {
    minHeight: 0,
    ...Platform.select({
      web: {
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      },
    }),
  },
  planFile: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginHorizontal: -4,
    marginTop: -4,
    paddingHorizontal: 4,
    paddingVertical: 4,
    borderRadius: 8,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  planFileIcon: {
    width: 30,
    height: 30,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  planFileCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  planFileTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  planActions: {
    gap: 8,
    flexShrink: 0,
  },
  planAction: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    height: 28,
    paddingHorizontal: 10,
    borderRadius: 8,
    borderWidth: 1,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  planHint: {
    lineHeight: 16,
  },
  planTrack: {
    height: 3,
    borderRadius: 999,
    overflow: 'hidden',
  },
  planFill: {
    height: '100%',
    borderRadius: 999,
  },
  planStepsScroll: {
    maxHeight: 180,
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
  },
  planSteps: {
    gap: 7,
    paddingBottom: 2,
  },
  planStep: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 9,
  },
  planStepIcon: {
    marginTop: 3,
  },
  planStepBody: {
    flex: 1,
    minWidth: 0,
    gap: 1,
  },
  planStepDone: {
    textDecorationLine: 'line-through',
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  approval: {
    alignItems: 'flex-start',
    ...Platform.select({ web: { cursor: 'default' } }),
  },
  approvalBody: {
    flex: 1,
    gap: 4,
  },
  approvalIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  approvalHeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  hostPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    maxWidth: 220,
  },
  approvalActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 6,
    flexWrap: 'wrap',
  },
  creditBox: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 11,
    gap: 9,
    marginTop: 8,
  },
  creditHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
  },
  creditHeadline: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  creditTrack: {
    height: 4,
    borderRadius: 999,
    overflow: 'hidden',
  },
  creditFill: {
    height: '100%',
    borderRadius: 999,
  },
  creditActions: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  questionCard: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 6,
    minHeight: 0,
    ...Platform.select({
      web: {
        cursor: 'default',
        maxHeight: '40vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      },
      default: { maxHeight: 300 },
    }),
  },
  questionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    flexShrink: 0,
  },
  questionActions: {
    flexShrink: 0,
  },
  eyebrow: {
    letterSpacing: 0.6,
  },
  stepDots: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    marginLeft: 'auto',
  },
  stepDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
  },
  questionText: {
    lineHeight: 18,
  },
  // Scroll the prompt and answers together. Keeping the answers outside the
  // scroll area lets four stacked options squeeze the prompt down to nothing.
  questionContentScroll: {
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
  },
  questionContent: {
    gap: 6,
  },
  questionBodyContent: {
    flexShrink: 0,
    gap: 4,
    paddingBottom: 0,
  },
  questionBody: {
    lineHeight: 18,
    ...Platform.select({ web: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', userSelect: 'text' } }),
  },
  optionWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  optionStack: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 4,
    width: '100%',
    minWidth: 0,
  },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 5,
    maxWidth: '100%',
    ...Platform.select({ web: { cursor: 'pointer', transition: 'background-color 0.15s ease, border-color 0.15s ease' } }),
  },
  chipSpinner: {
    width: 12,
    height: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 6,
    alignSelf: 'stretch',
    minWidth: 0,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'background-color 0.15s ease, border-color 0.15s ease' } }),
  },
  optionIndex: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionSpinner: {
    transform: [{ scale: 0.55 }],
  },
  optionMuted: {
    opacity: 0.45,
  },
  optionLabel: {
    flexShrink: 1,
    minWidth: 0,
  },
  customBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderRadius: 8,
    paddingLeft: 10,
    paddingRight: 4,
    paddingVertical: 3,
    marginTop: 4,
    overflow: 'hidden',
  },
  customInputWrap: {
    flex: 1,
    minWidth: 0,
  },
  customInput: {
    width: '100%',
    paddingVertical: Platform.OS === 'web' ? 3 : 2,
    fontSize: 13,
    ...Platform.select({ web: { outlineStyle: 'none', borderWidth: 0 } }),
  },
  sendButton: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    flexShrink: 0,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'opacity 0.15s ease' } }),
  },
  sendButtonHovered: {
    opacity: 0.85,
  },
  questionFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginTop: 0,
  },
  skipButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 7,
    paddingVertical: 3,
    ...Platform.select({ web: { cursor: 'pointer', transition: 'background-color 0.15s ease, border-color 0.15s ease' } }),
  },
  skipHint: {
    flexShrink: 1,
    minWidth: 0,
  },
});
